import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { setGlobalConfig } from 'actions/global.config.action'
import { GlobalConfigType } from 'actions/global.config.type'
import { setLoanGlobalStatus } from 'actions/loan.action'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { defaultVisibleToAccountTypes, ILoanStatusConfig, LoanStatusType, setLoanStatusConfig } from 'config'
import { accountTypes, accountTypeWithBorrower } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getLoanTypeMapForSelect } from 'reducers/loanDetail.reducer'
import { getAdminConfig, setAdminConfig } from 'services'
import {
  ButtonGroup,
  HorzScrollContainer,
  MultiSelect,
  TextArea,
  Tooltip,
} from 'stories/components'
import { Checkbox } from 'stories/components/Checkbox/Checkbox'
import { confirm, fillObject, generateUniqeId, InputConvert, InputValidate, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constants'
import { LoanServicingStatus } from './LoanServicingStatus'
import { LoanSubStatusConfig } from './LoanSubStatus'

const loanStatusCategories: Record<string, string> = {
  isActive: 'Active',
  isArchived: 'Archived',
  isClose: 'Closed',
  isLoanSetupUnhold: 'Loan Setup On Hold',
  isSuspended: 'Approval (Suspense)',
  isBrokerCanResubmitConditions: 'Can Broker resubmit conditions?',
  isIncludedCalculation: 'Included in Status Bubbles Calculation',
  isIncludedProductionStatsCalculation: 'Included in Production Stats Calculation',
  confirmRequired: 'Need Confirmation',
}

const loanStatusColors: Record<string, string> = {
  isActive: 'green',
  isArchived: 'sky',
  isClose: 'yellow',
  isLoanSetupUnhold: 'orange',
  isSuspended: 'red',
  isBrokerCanResubmitConditions: 'purple',
  isIncludedCalculation: 'blue',
  isIncludedProductionStatsCalculation: 'indigo',
  confirmRequired: 'red',
}

const stickyColumns: Record<string, string> = {
  No: 'left-0 min-w-[3rem] w-12 max-w-[3rem]',
  'Origin Status': 'left-12 min-w-[11rem] w-44 max-w-[11rem]',
  'New Status': 'left-[14rem] min-w-[13rem] w-52 max-w-[13rem] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.25)]',
}

const stickyHeaderClass = (column: string) =>
  stickyColumns[column] ? `sticky z-[50] bg-gray-50 ${stickyColumns[column]}` : ''

const stickyBodyClass = (column: string) =>
  stickyColumns[column] ? `sticky z-[50] bg-white ${stickyColumns[column]}` : ''

const loanStatusDescriptions: Record<string, string> = {
  isActive: 'Active Loan Status which is used in Pipeline.',
  isArchived: 'Archived Loan Status which is used in Pipeline.',
  isClose:
    'Clear to Close Loan Status. These loans can use Closing Calendar page and can use `Generate Commitment` in Loan Submission page.',
  isLoanSetupUnhold: 'Loan Status which can Submit loan in Loan Submission page.',
  isSuspended: 'Loan Status which can Generate Approval(Suspense) in Loan Submission page.',
  isBrokerCanResubmitConditions: 'Loan Status which can Submit Conditions in Loan Submission page.',
  isIncludedCalculation: 'Loan Status which is included in Status Bubbles Calculation.',
  isIncludedProductionStatsCalculation: 'Loan Status which is included in Production Stats Calculation.',
  confirmRequired: 'Loan Status which needs confirmation before status change.',
}

const ConfirmTextsModal = ({
  statusLabel,
  initialText1,
  hasAutoNote: _hasAutoNote,
  onSave,
  onClose,
}: {
  statusLabel: string
  initialText1: string
  hasAutoNote: boolean
  onSave: (text1: string, hasAutoNote: boolean) => void
  onClose: () => void
}) => {
  const [text1, setText1] = useState(initialText1)
  const [hasAutoNote, setHasAutoNote] = useState(_hasAutoNote)

  return (
    <>
      <div className="fixed inset-0 bg-black opacity-25" style={{ zIndex: 40 }} />
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 50 }}>
        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">Confirm Texts — {statusLabel}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Confirmation Message <span className="text-gray-400">(shown to user before status change)</span>
              </p>
              <TextArea title="" value={text1} onChange={(v) => setText1(v)} rows={4} />
            </div>
            <div>
              <Checkbox
                color="gray"
                id={`${statusLabel}-hasAutoNote`}
                title="Auto Note (created as a note after status change)"
                value={hasAutoNote}
                onChange={(value) => setHasAutoNote(value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 border-t">
            <button onClick={onClose} className="text-red-500 hover:underline font-semibold text-sm px-4 py-2">
              Cancel
            </button>
            <button
              onClick={() => onSave(text1, hasAutoNote)}
              className="bg-primary text-white hover:bg-sky-700 font-medium rounded-lg text-sm px-5 py-2"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

const publishLoanStatusConfig = (config: Record<string, ILoanStatusConfig>, dispatch: (action: any) => void) => {
  const labels: Record<string, string> = {}
  Object.keys(config).forEach((key) => {
    if (config[key].use && config[key].new) labels[key] = config[key].new
  })
  setLoanStatusConfig(config)
  dispatch(setLoanGlobalStatus(labels as any))
  dispatch(setGlobalConfig(GlobalConfigType.LoanStatus, config))
}

const LoanStatusLabel = () => {
  const dispatch = useDispatch()
  const [isLoading, setLoading] = useState(true)
  const [data, setData] = useState<Record<string, ILoanStatusConfig>>({})

  const [loanGlobalStatus] = useSelector((state: any) => [state.loanGlobalStatus])
  const [inputs, setInputs] = useState(defaultInputs(loanGlobalStatus))
  const [sortKeys, setSortKeys] = useState<string[]>([])
  const [confirmModalKey, setConfirmModalKey] = useState<string | null>(null)

  const defaultNonServicingStatus = fillObject(Object.keys(loanGlobalStatus), false)

  const initData = async () => {
    const [data, settingData] = await Promise.all([getAdminConfig('loanStatus'), getAdminConfig('globalSettingsV2')])

    const tempData = cloneDeep(data)
    Object.keys(tempData).forEach((key) => {
      const item = tempData[key]
      if (item.isIncludedCalculation === undefined) {
        item.isIncludedCalculation = true
      }
      if (item.isIncludedProductionStatsCalculation === undefined) {
        item.isIncludedProductionStatsCalculation = key === 'funded'
      }
      if (item.visibleTo === undefined) {
        item.visibleTo = defaultVisibleToAccountTypes()
      }
    })

    setData(tempData)

    const newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = settingData[key]
    })
    setInputs(newInputs)
    setLoading(false)
  }

  useEffect(() => setSortKeys(Object.keys(Array(Object.keys(data).length).fill(1))), [data])

  useEffect(() => {
    initData()
  }, [])

  const onSync = async (_data?: any) => {
    setLoading(true)
    const next = _data || data
    await setAdminConfig('loanStatus', next)
    publishLoanStatusConfig(next, dispatch)
    setLoading(false)
  }

  const onChange = (key: string, value: any, map: string) => {
    let temp = cloneDeep(data)
    if (temp[key] === undefined) temp[key] = {} as any
    ;(temp[key] as any)[map] = value
    if (map === 'new' && value.length === 0) temp[key]['use'] = false
    setData(temp)
    if (!['fromPermission', 'toPermission', 'visibleTo', 'channels'].includes(map)) onSync(temp)
  }

  const onAdd = async () => {
    const title = await prompt('Please enter the new title')
    if (!title) return
    const newData = cloneDeep(data)
    const newKey = generateUniqeId(title, Object.keys(data))
    if (!newKey) return

    newData[newKey] = {
      origin: '',
      new: title,
      use: true,
      fromPermission: Object.keys(accountTypes) as any,
      toPermission: Object.keys(accountTypes) as any,
      visibleTo: defaultVisibleToAccountTypes(),
      channels: Object.keys(getLoanTypeMapForSelect()),
      isActive: false,
      isIncludedCalculation: true,
      isIncludedProductionStatsCalculation: false,
    }
    setData(newData)
    onSync(newData)
  }

  const onRemove = async (key: string) => {
    const result = await confirm('Are you sure you want to remove this item?')
    if (!result) return
    const newData = cloneDeep(data)
    delete newData[key]
    setData(newData)
    onSync(newData)
  }

  const onChangeInput = async (key: string, value: any) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onConfirmRequiredChange = (key: string, value: boolean) => {
    const temp = cloneDeep(data)
    if (temp[key] === undefined) temp[key] = {} as any
    temp[key].confirmRequired = value
    setData(temp)
    onSync(temp)
    if (value) setConfirmModalKey(key)
  }

  const onSaveConfirmTexts = (text1: string, hasAutoNote: boolean) => {
    if (!confirmModalKey) return
    const temp = cloneDeep(data)
    temp[confirmModalKey].confirmText1 = text1
    temp[confirmModalKey].hasAutoNote = hasAutoNote
    setData(temp)
    onSync(temp)
    setConfirmModalKey(null)
  }

  const onChangeOrder = async (toIndex: number, fromIndex: number) => {
    const keys = Object.keys(data)

    const newKeys = cloneDeep(keys)
    const [movedKey] = newKeys.splice(fromIndex, 1)
    newKeys.splice(toIndex, 0, movedKey)

    const newData: any = {}
    newKeys.forEach((key) => (newData[key] = data[key]))

    setData(newData)
    onSync(newData)
  }

  const onSubmit = async () => {
    let hasError = false
    let newInputs = cloneDeep(inputs)

    const rtl: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      rtl[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return

    setLoading(true)
    await setAdminConfig('globalSettingsV2', rtl)
    setLoading(false)
  }

  const renderControls = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {Object.keys(inputs).map((key, index) => {
          let input = inputs[key]
          if (['nonServicingLoanStatus', 'borrowerLoanStatus'].includes(key))
            input.value = { ...defaultNonServicingStatus, ...input.value }

          return (
            <div className={`input md:col-span-${input.span || 1} `} key={index}>
              <RenderInput input={input} Key={key} onChange={onChangeInput} onBlur={() => onSubmit()} />
            </div>
          )
        })}
      </div>
    )
  }

  const renderLoanStatusDescriptions = () => {
    return (
      <div className="mt-4 border rounded-lg p-4 bg-gray-100">
        {Object.keys(loanStatusDescriptions).map((key, index) => (
          <p className={`text-${loanStatusColors[key]}-500`}>
            {index + 1}. {loanStatusCategories[key]}: {loanStatusDescriptions[key]}
          </p>
        ))}
      </div>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      {renderControls()}

      <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center`}>
        <p>- Origination Pipeline</p>
        <div className="flex">
          <span className="btn-icon" onClick={onAdd}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>
      </div>
      <div className="mt-4 loan-status-label-table [&_.overflow-y-hidden]:!overflow-y-visible [&_td]:overflow-visible [&_td]:align-top">
        <HorzScrollContainer size={Object.keys(data).length} leftInset="27rem">
          <table className="text-sm min-w-max w-full">
            <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
              <tr>
                {['No', 'Origin Status', 'New Status', 'In Use', 'Channel', 'Visible To', 'From', 'To', 'Status', 'Order'].map(
                  (v) => (
                    <th scope="col" className={`p-3 border whitespace-nowrap ${stickyHeaderClass(v)}`} key={`header-${v}`}>
                      {v === 'Channel' ? (
                        <Tooltip message="This status is offered on the loan status list only for the selected channels. Every channel starts selected.">
                          <span>Channel</span>
                        </Tooltip>
                      ) : (
                        v
                      )}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {Object.keys(data).map((key, index) => {
                const item = data[key] || {}
                const isCustom = !Object.values(LoanStatusType).includes(key as any)
                return (
                  <tr
                    key={index}
                    className={
                      index >= Object.keys(data).length - 6
                        ? '[&_.absolute.z-40]:bottom-full [&_.absolute.z-40]:top-auto [&_.absolute.z-40]:mb-0'
                        : ''
                    }
                  >
                    <td className={`p-2 border ${stickyBodyClass('No')}`}>{index + 1}</td>
                    <td className={`px-2 border ${stickyBodyClass('Origin Status')}`} title={item.origin}>
                      <span className="block truncate">{item.origin}</span>
                    </td>
                    <td className={`px-2 border ${stickyBodyClass('New Status')}`}>
                      <PlainInput
                        value={item.new}
                        content={item.new}
                        onChange={(newValue: string) => onChange(key, newValue, 'new')}
                      />
                    </td>
                    <td className="px-2 border text-center">
                      <div className="flex items-center">
                        <Checkbox
                          color="gray"
                          id={key}
                          title={''}
                          key={key}
                          value={item.use}
                          onChange={(value) => onChange(key, value, 'use')}
                        />
                        {isCustom && (
                          <div className="flex">
                            <span className="btn-icon" onClick={() => onRemove(key)}>
                              <TrashIcon className="w-4 h-4 text-red-500" />
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 border text-center">
                      <MultiSelect
                        id={`${key}-channels`}
                        title="Channel"
                        options={getLoanTypeMapForSelect()}
                        value={item.channels ?? Object.keys(getLoanTypeMapForSelect())}
                        onChange={(selected) => onChange(key, selected, 'channels')}
                        onBlur={onSync}
                        className="w-56"
                      />
                    </td>
                    <td className="px-2 border text-center">
                      <MultiSelect
                        id={`${key}-visibleTo`}
                        title="Account Type"
                        options={accountTypeWithBorrower()}
                        value={item.visibleTo || defaultVisibleToAccountTypes()}
                        onChange={(selected) => onChange(key, selected, 'visibleTo')}
                        onBlur={onSync}
                        className="w-64"
                      />
                    </td>
                    <td className="px-2 border text-center">
                      <MultiSelect
                        id={`${key}-fromPermission`}
                        title="Account Type"
                        options={accountTypes}
                        value={item.fromPermission || []}
                        onChange={(selected) => onChange(key, selected, 'fromPermission')}
                        onBlur={onSync}
                        className="w-64"
                      />
                    </td>
                    <td className="px-2 border text-center">
                      <MultiSelect
                        id={`${key}-toPermission`}
                        title="Account Type"
                        options={accountTypes}
                        value={item.toPermission || []}
                        onChange={(selected) => onChange(key, selected, 'toPermission')}
                        onBlur={onSync}
                        className="w-64"
                      />
                    </td>
                    <td className="px-2 border text-center">
                      <div className="grid grid-flow-col grid-rows-2 items-center gap-2">
                        {Object.keys(loanStatusCategories).map((type) => (
                          <Tooltip message={loanStatusCategories[type]} key={`${key}-${type}`}>
                            {type === 'confirmRequired' ? (
                              <div className="flex items-center gap-1">
                                <Checkbox
                                  color={loanStatusColors[type]}
                                  id={`${key}-${type}`}
                                  title={''}
                                  value={!!item.confirmRequired}
                                  onChange={(value) => onConfirmRequiredChange(key, value)}
                                />
                                {item.confirmRequired && (
                                  <button
                                    className="text-xs text-shade-blue underline"
                                    onClick={() => setConfirmModalKey(key)}
                                  >
                                    Edit
                                  </button>
                                )}
                              </div>
                            ) : (
                              <Checkbox
                                color={loanStatusColors[type]}
                                id={`${key}-${type}`}
                                title={''}
                                value={(item as any)[type]}
                                onChange={(value) => onChange(key, value, type)}
                              />
                            )}
                          </Tooltip>
                        ))}
                      </div>
                    </td>

                    <td className="px-2 border text-center">
                      <select
                        value={index}
                        className="text-sm py-1 border-gray-200 w-16"
                        onChange={(e) => onChangeOrder(parseInt(e.target.value), index)}
                      >
                        {sortKeys.map((value) => (
                          <option value={value} key={`option-${value}`}>
                            {parseInt(value) + 1}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </HorzScrollContainer>
      </div>
      {renderLoanStatusDescriptions()}
      <div className="mt-8">
        <LoanSubStatusConfig data={data} />
      </div>

      {confirmModalKey && (
        <ConfirmTextsModal
          statusLabel={data[confirmModalKey]?.new || confirmModalKey}
          initialText1={data[confirmModalKey]?.confirmText1 || ''}
          hasAutoNote={data[confirmModalKey]?.hasAutoNote || false}
          onSave={onSaveConfirmTexts}
          onClose={() => setConfirmModalKey(null)}
        />
      )}
    </div>
  )
}

const tabs = {
  origination: 'Origination',
  servicing: 'Servicing',
}

export const LoanStatusLabelPage = () => {
  const [tab, setTab] = useState<keyof typeof tabs>('origination')

  return (
    <div>
      <div className="mb-4">
        <ButtonGroup title={tabs} value={tab} onChange={(v: string) => setTab(v as keyof typeof tabs)} />
      </div>
      {tab === 'origination' && <LoanStatusLabel />}
      {tab === 'servicing' && <LoanServicingStatus />}
    </div>
  )
}
